import { ContactabilityStatus } from 'globalTypes';
import { cloneDeep, memoize } from 'lodash';

import ChatMemberships from 'collections/ChatMemberships';
import { ReportOptions, ReportOtherOption } from 'constants/ReportConstants';
import ChatMembership from 'entities/ChatMembership';
import ChatMessage from 'entities/ChatMessage';
import { PotentialOrRegularChatMember } from 'entities/PotentialChatMember';
import apiRequest from 'lib/apiRequest';
import Entity from 'lib/Entity';
import { DenialReason } from 'phoneCalls/components/PhoneCallModal';

import { Backbone$Id } from 'types/typescript/Backbone';

export const TYPES = {
  ACTIVITY: 'activity',
  GROUP: 'group',
  INDIVIDUAL: 'individual',
  ASSISTANT: 'assistant',
};

export type ChatTypeType = 'activity' | 'group' | 'individual' | 'assistant';
export const MAX_PARTICIPANTS_REPLIES_ENABLE = 200;

export type OtherMemberType = {
  id: Backbone$Id;
  initials: string;
  profile_picture_url?: string;
  color: string; // 6-char hex code
  contactability_status: ContactabilityStatus;
  name: string;
  uuid?: string;
};

class Chat extends Entity {
  initialize() {
    this.idAttribute = 'uuid';
    this.urlRoot = '/chats';
  }

  defaults() {
    return {
      active: true,
      can_close: true,
      can_open: false,
      group_summaries: [],
      memberships: [],
      last_read_seq: null,
      present: true,
      state: 'open',
      can_send: true,
      updated_at: new Date(),
    };
  }

  parse(response: any) {
    const chat = cloneDeep(response);

    if (chat['last_message']) {
      chat['last_message'] = ChatMessage.fromJSON(chat['last_message']);
    }

    chat['memberships'] = ChatMemberships.fromJSON<ChatMemberships>(chat['memberships']);
    chat['updated_at'] = new Date(chat['updated_at']);
    return chat;
  }

  isActive(): boolean {
    return !!this.get('active');
  }

  hasTitle(): boolean {
    return this.get('title').length > 0;
  }

  getTitle(): string {
    return this.get('title');
  }

  getQueryKey() {
    return this.get('query_key');
  }

  canAdd(): boolean {
    return !!this.get('can_add');
  }

  canManageReplies(): boolean {
    return !!this.get('can_manage_replies');
  }

  canOpen(): boolean {
    return !!this.get('can_open');
  }

  canPhone(): boolean {
    return !!this.get('can_phone');
  }

  getCanPhoneDenialReason(): DenialReason {
    return this.get('can_phone_denial_reason');
  }

  canLeave(): boolean {
    return !!this.get('can_leave');
  }

  canSend(): boolean {
    return !!this.get('can_send');
  }

  hasRepliesDisabled(): boolean {
    return this.getState() === 'replies_disabled';
  }

  isInGroup(groupId: Backbone$Id) {
    return !!this.get('group_summaries').find((group: any) => group.id === groupId);
  }

  isRemoved(): boolean {
    return this.getState() === 'removed';
  }

  isDead(): boolean {
    return this.getState() === 'dead';
  }

  hasLeft(): boolean {
    return this.getState() === 'left';
  }

  isOpen(): boolean {
    return this.getState() === 'open';
  }

  getMaximumMembers(): number {
    return this.get('maximum_members');
  }

  hasReachedParticipantNumberLimit(): boolean {
    return this.getPresentMemberships().length >= this.getMaximumMembers();
  }

  // NOTE: Not to be confused with the !this.isActive()
  isInactive(): boolean {
    return this.hasLeft() || this.isRemoved() || this.isDead();
  }

  getCreatorId() {
    return this.get('creator_id');
  }

  getType(): ChatTypeType | undefined {
    return this.get('type');
  }

  isActivityType(): boolean {
    return this.getType() === TYPES.ACTIVITY;
  }

  isAssistantType(): boolean {
    return this.getType() === TYPES.ASSISTANT;
  }

  isGroupType(): boolean {
    return this.getType() === TYPES.GROUP;
  }

  isIndividualType(): boolean {
    return this.getType() === TYPES.INDIVIDUAL;
  }

  getLastMessage(): ChatMessage | undefined {
    return this.get('last_message');
  }

  // TODO: Get rid of this (immutable data)
  setLastMessage(message: ChatMessage) {
    return this.set({ last_message: message });
  }

  // legacy state
  isPaused(): boolean {
    return this.getState() === 'paused';
  }

  // legacy state
  isClosed(): boolean {
    return this.getState() === 'closed';
  }

  // We still received pusher update on groups
  // with the old states, this helper handles it.
  isDisabled(): boolean {
    return this.isPaused() || this.hasRepliesDisabled() || this.isClosed();
  }

  isInsufficientlyConnected(): boolean {
    return this.getState() === 'insufficiently_connected';
  }

  hasMutedMembers(): boolean {
    return !!this.get('has_muted_members');
  }

  getGroupSummaries() {
    return this.get('group_summaries');
  }

  getAssociatedGroupUuids() {
    return this.getGroupSummaries().map((summary: any) => summary.uuid);
  }

  // HACK param because of circular dependencies
  markAsReadForAssociatedGroups(unreadStore: any) {
    const unreadCount = this.getUnreadMessages();
    if (!unreadCount) {
      return;
    }

    const groupUuids = this.getAssociatedGroupUuids();
    groupUuids.forEach((uuid: any) => unreadStore.decrementUnreads(uuid, unreadCount));
  }

  getLastReadSeq(): number {
    return this.get('last_read_seq');
  }

  // Note: may not be loaded if the chat is loaded by v2/chats
  getMemberships(): ChatMemberships | ChatMembership[] {
    return this.get('memberships');
  }

  getMembershipsCount() {
    return this.get('memberships_count');
  }

  // Note: may not be loaded if the chat is loaded by v2/chats
  getMemberIds(): number[] {
    const memberships = this.getMemberships();
    if (memberships instanceof ChatMemberships) {
      return memberships.map((membership: ChatMembership) => membership.getMember().id);
    }
    return memberships.map((membership: ChatMembership) => membership.getMember().id);
  }

  // Chats now have origin_ids which are the groups the chat should be attached to
  // (For example, what group was the user in when they created the chat?)
  getOriginIds() {
    return this.get('origin_ids');
  }

  isPresent(): boolean {
    return this.get('present');
  }

  doFilter(
    memberships: ChatMemberships | ChatMembership[] | undefined,
    filterFunc: (arg0: ChatMembership) => boolean
  ): ChatMembership[] {
    if (memberships instanceof ChatMemberships) {
      return memberships.filter(filterFunc);
    } else if (memberships) {
      return memberships.filter(filterFunc);
    }
    return [];
  }

  getLeftMemberships(): ChatMembership[] {
    return this.doFilter(this.getMemberships(), (membership) => membership.getState() === 'left');
  }

  getPresentMemberships(): ChatMembership[] {
    return this.doFilter(
      this.getMemberships(),
      (membership) => membership.getState() === 'present'
    );
  }

  getOtherPresentMemberships(currentUserId: Backbone$Id | undefined): ChatMembership[] {
    return this.doFilter(
      this.getMemberships(),
      (membership: any) =>
        membership.getState() === 'present' && membership.getMember().id !== currentUserId
    );
  }

  getOtherChatMemberships(allowEmpty: boolean = false) {
    // XXX remove CurrentUser from entities
    const CurrentUser = require('auth/CurrentUser').default;
    const currentUserId = CurrentUser.getUserId();
    const memberships = new ChatMemberships(
      this.doFilter(
        this.getMemberships(),
        (membership: any) => membership.getMember().id !== currentUserId
      )
    );

    // Note: this is a temporary fix until the backend fixes issues with deleted chat memberships
    return memberships.length > 0 || allowEmpty ? memberships : this.getMemberships();
  }

  getOtherChatMembers(allowEmpty: boolean = false): PotentialOrRegularChatMember[] {
    const otherChatMemberships = this.getOtherChatMemberships(allowEmpty);
    if (otherChatMemberships instanceof ChatMemberships) {
      return otherChatMemberships.map((membership) => membership.getMember());
    }
    return otherChatMemberships.map((membership) => membership.getMember());
  }

  getOtherPresentChatMembers(allowEmpty: boolean = false): PotentialOrRegularChatMember[] {
    const otherChatMemberships = this.getOtherChatMemberships(allowEmpty);
    if (otherChatMemberships instanceof ChatMemberships) {
      otherChatMemberships
        .filter((membership: any) => {
          return membership.getState() === 'present';
        })
        .map((membership: any) => {
          return membership.getMember();
        });
    }

    return otherChatMemberships
      .filter((membership: any) => {
        return membership.getState() === 'present';
      })
      .map((membership: any) => {
        return membership.getMember();
      });
  }

  getOwnerChatMembership(): ChatMembership | undefined {
    return this.doFilter(
      this.getMemberships(),
      (membership) => membership.getMember().id === this.getCreatorId()
    )[0];
  }

  getOtherChatMembershipsWithState(state: string) {
    const otherChatMemberships = this.getOtherChatMemberships();
    if (otherChatMemberships instanceof ChatMemberships) {
      return otherChatMemberships.reduce((results: any, membership: ChatMembership) => {
        if (!state || membership.getState() === state) {
          results.push(membership);
        }
        return results;
      }, []);
    }
    return otherChatMemberships.reduce((results: any, membership: ChatMembership) => {
      if (!state || membership.getState() === state) {
        results.push(membership);
      }
      return results;
    }, []);
  }

  getMembership(member: PotentialOrRegularChatMember): ChatMembership | undefined {
    const memberships = this.getMemberships().filter((membership: any) => {
      return membership.getMember().id === member.id;
    });

    return memberships[0];
  }

  isPresentMember(member: PotentialOrRegularChatMember): boolean {
    const membership = this.getMembership(member);
    return !!membership && membership.isPresent();
  }

  createNewMembership(member: PotentialOrRegularChatMember): void {
    const memberships = this.getMemberships();
    if (memberships instanceof ChatMemberships) {
      memberships.add(ChatMembership.create(member));
    } else {
      memberships.push(ChatMembership.create(member));
    }
    this.addToMemberships(ChatMembership.create(member));
  }

  addToMemberships(membership: ChatMembership): void {
    const memberships = this.getMemberships();
    if (memberships instanceof ChatMemberships) {
      memberships.add(membership);
    } else {
      memberships.push(membership);
    }
  }

  removeFromMemberships(membership: ChatMembership): void {
    const memberships = this.getMemberships();
    if (memberships instanceof ChatMemberships) {
      memberships.remove(membership);
    } else {
      this.set(
        'memberships',
        memberships.filter((m) => m.id !== membership.id)
      );
    }
  }

  /* DEPRECATED, use isGroupType */
  isGroupChat(): boolean {
    return this.getMemberships().length > 2;
  }

  getState(): string {
    return this.get('state');
  }

  getColor(): string | undefined {
    return this.get('color');
  }

  getUnreadMessages() {
    return this.get('unread_messages');
  }

  getUpdatedAt(): Date {
    return this.get('updated_at');
  }

  // TODO: Get rid of this (immutable data)
  setUpdatedAt(date: Date): void {
    this.set({ updated_at: date });
  }

  isUnsubscribed(): boolean {
    return this.get('state') === 'dead';
  }

  // TODO: Get rid of this? (immutable data)
  markAsRead(): void {
    this.set({ unread_messages: 0 });
    const lastMessage = this.getLastMessage();
    if (lastMessage) {
      this.set({ last_read_seq: lastMessage.getSeq() });
    }
  }

  deactivate(): void {
    this.set({ active: false });
  }

  leave() {
    return this.save(
      {
        active:
          (this.getMemberships() as ChatMemberships).where({ state: 'present' }).length - 1 > 1,
        can_open: true,
        present: false,
      },
      {
        data: { chat: { present: false } },
        patch: true,
      }
    );
  }

  initiate(message?: ChatMessage) {
    const chat: {
      message?: { body?: string; file?: File; uuid: string };
      origin_ids: any[];
      member_ids: number[];
    } = {
      origin_ids: this.getOriginIds(),
      member_ids: this.getMemberIds(),
      message: undefined,
    };
    if (message) {
      chat.message = {
        body: message.getBody(),
        file: message.getFile(),
        uuid: message.getUuid(),
      };
    }
    return apiRequest('/chats', 'POST', {
      chat,
    }).then((newChat) => Chat.fromJSON(newChat) as Chat);
  }

  isOutsideOfOtherOfficeHours(): boolean {
    return this.getOtherPresentChatMembers().some((member) => {
      const officeHours = member.getOfficeHours();
      if (officeHours && !officeHours.isAvailableNow()) {
        return true;
      }
      return false;
    });
  }

  getOtherMember(): OtherMemberType | undefined {
    return this.get('other_member');
  }

  getSortKey(): string {
    return this.get('sort_key');
  }

  // Extract the timestamp from sort_key. If it fails, fall back to updated_at
  getSortKeyTimestamp(): Date {
    try {
      const parsedDate = new Date(this.getSortKey().split('UTC')[0] + 'UTC');
      if (isNaN(parsedDate.getTime())) {
        // Detect 'Invalid Date'
        return this.getUpdatedAt();
      }
      return parsedDate;
    } catch (e) {} // eslint-disable-line
    return this.getUpdatedAt();
  }

  addMembers(membersIds: Array<Backbone$Id>): Promise<ChatMembership[]> {
    return apiRequest(`/chats/${this.id}/members`, 'POST', {
      member_ids: membersIds,
    }).then((memberships) => {
      return memberships.map((m: any) => ChatMembership.fromJSON(m));
    });
  }

  changeMemberStatus(membersId: Backbone$Id, state: string): Promise<ChatMembership> {
    return apiRequest(`/chats/${this.id}/members/${membersId}`, 'PATCH', {
      member: { state },
    }).then((membership: any) => {
      return ChatMembership.fromJSON(membership) as ChatMembership;
    });
  }

  disableReplies(): Promise<Chat> {
    return apiRequest(`/chats/${this.id}`, 'PATCH', {
      chat: { state: 'replies_disabled' },
    }).then((chat) => Chat.fromJSON(chat) as Chat);
  }

  enableReplies(): Promise<Chat> {
    return apiRequest(`/chats/${this.id}`, 'PATCH', {
      chat: { state: 'open' },
    }).then((chat) => Chat.fromJSON(chat) as Chat);
  }

  updateTitle(title: string): Promise<void> {
    return this.save(
      {
        chat: { title },
      },
      {
        patch: true,
      }
    );
  }

  // memoize so this calculation doesn't create a new object
  // and bust the shallow compare.
  getDisplayNames: Function = memoize(() => {
    const present = this.getOtherChatMembershipsWithState('present');
    let names = present.map((membership: ChatMembership) => {
      return membership.getMember().getName();
    });

    if (names.length === 0) {
      const ownerMembership = this.getOwnerChatMembership();
      if (ownerMembership) {
        names = [ownerMembership.getMember().getName()];
      }
    }

    return names;
  });

  static createLocal(members: PotentialOrRegularChatMember[], title: string, originIds = []): Chat {
    const memberships = new ChatMemberships(
      members.map((member: PotentialOrRegularChatMember) => ChatMembership.create(member))
    );
    const otherChatMember = memberships.first().getMember();

    return new Chat({
      memberships,
      other_member: {
        id: -1 /* wtf? */,
        uuid: otherChatMember.getUuid(),
        color: otherChatMember.getColor() || '',
        initials: otherChatMember.getInitials() || '',
        contactability_status: otherChatMember.getContactabilityStatus(),
        name: '',
      },
      origin_ids: originIds,
      title,
      type: members.length === 2 ? TYPES.INDIVIDUAL : TYPES.GROUP,
    });
  }

  static markReadUpto(chatId: Backbone$Id, seq: number): Promise<void> {
    return apiRequest(`/chats/${chatId}`, 'PATCH', {
      chat: {
        last_read_seq: seq,
      },
    });
  }

  static emailTranscript(chatId: Backbone$Id): Promise<void> {
    return apiRequest(`/chats/${chatId}/email_transcript`, 'POST', {});
  }

  static reportChat(
    chatId: Backbone$Id,
    uuid: string,
    key: string,
    notes: string | undefined
  ): Promise<void> {
    const reason = ReportOptions[key] || ReportOtherOption;
    const reasonAndNotes = notes ? `${reason} - ${notes}` : reason;
    return apiRequest(`/chats/${chatId}/report`, 'POST', {
      uuid: chatId,
      chat: {
        key,
        reason: reasonAndNotes,
        reported_user_uuid: uuid,
      },
    });
  }
}

export default Chat;
